import React, { useCallback, useMemo } from 'react';
import { createEditor } from 'slate';
import { withHistory } from 'slate-history';
import { Editable, Slate, withReact, RenderElementProps, RenderLeafProps } from 'slate-react';
import isHotkey from 'is-hotkey';
import styled from 'styled-components';

import { Element, Leaf } from './Elements';
import Toolbar from './Toolbar';
import type { RichTextEditorProps, MarkType } from './types';
import { TOOLBAR_PRESETS } from './types';
import { toggleMark } from './utils';

const HOTKEYS: Record<string, MarkType> = {
  'mod+b': 'bold',
  'mod+i': 'italic',
  'mod+u': 'underline',
};

const EditorContainer = styled.div<{ $minHeight: number; $maxHeight: number; $disabled?: boolean }>`
  border: 1px solid rgba(59, 111, 217, 0.15);
  border-radius: 12px;
  background: #ffffff;
  overflow: hidden;
  transition: all 0.2s ease;
  opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};

  &:focus-within {
    border-color: #3B6FD9;
    box-shadow: 0 0 0 3px rgba(59, 111, 217, 0.1);
  }

  &:hover:not(:focus-within) {
    border-color: rgba(59, 111, 217, 0.3);
  }
`;

const EditableWrapper = styled.div<{ $minHeight: number; $maxHeight: number }>`
  min-height: ${({ $minHeight }) => $minHeight}px;
  max-height: ${({ $maxHeight }) => $maxHeight}px;
  overflow-y: auto;
  padding: 16px;

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(59, 111, 217, 0.2);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgba(59, 111, 217, 0.3);
  }
`;

const StyledEditable = styled(Editable)`
  outline: none;
  font-size: 0.9375rem;
  line-height: 1.7;
  color: #374151;

  p {
    margin: 0;
  }

  [data-slate-placeholder="true"] {
    color: #9ca3af;
    font-style: italic;
    opacity: 1 !important;
  }
`;

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder = 'Start typing...',
  disabled = false,
  autoFocus = false,
  preset = 'thread',
  customPreset,
  minHeight = 100,
  maxHeight = 400,
  className,
  onKeyDown: customOnKeyDown,
}) => {
  const editor = useMemo(() => withHistory(withReact(createEditor())), []);

  const toolbarPreset = customPreset || TOOLBAR_PRESETS[preset];
  const showToolbar = toolbarPreset.marks.length > 0 || toolbarPreset.blocks.length > 0 || toolbarPreset.enableLinks;

  const renderElement = useCallback((props: RenderElementProps) => <Element {...props} />, []);

  const renderLeaf = useCallback((props: RenderLeafProps) => <Leaf {...props} />, []);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (customOnKeyDown) {
        customOnKeyDown(event);
        if (event.defaultPrevented) {
          return;
        }
      }

      for (const hotkey in HOTKEYS) {
        if (isHotkey(hotkey, event as unknown as KeyboardEvent)) {
          event.preventDefault();
          const mark = HOTKEYS[hotkey];
          if (toolbarPreset.marks.includes(mark)) {
            toggleMark(editor, mark);
          }
        }
      }
    },
    [editor, toolbarPreset.marks, customOnKeyDown]
  );

  return (
    <Slate editor={editor} initialValue={value} onChange={onChange}>
      <EditorContainer
        $minHeight={minHeight}
        $maxHeight={maxHeight}
        $disabled={disabled}
        className={className}
      >
        {showToolbar && <Toolbar preset={toolbarPreset} disabled={disabled} />}
        <EditableWrapper $minHeight={minHeight} $maxHeight={maxHeight}>
          <StyledEditable
            renderElement={renderElement}
            renderLeaf={renderLeaf}
            placeholder={placeholder}
            spellCheck
            autoFocus={autoFocus}
            readOnly={disabled}
            onKeyDown={handleKeyDown}
          />
        </EditableWrapper>
      </EditorContainer>
    </Slate>
  );
};

export default RichTextEditor;
